import React, { ReactElement, useState } from 'react';
import { AccessibilityProps } from 'react-native';
import styled from 'styled-components';

import { useInputFocusContext } from 'components/forms/ResponseOptions/providers/InputFocusProvider';
import { GenericInputIcon } from 'components/generic-question/GenericInputButton';
import { ControlProps } from 'components/layout/Content/Control';
import { getControlThemes } from 'components/layout/Content/Control/styled';
import { printableProps } from 'components/layout/lib/printableProps';
import { useGetDynamicInputStyles } from 'components/ResponsiveLayout';
import { ThemeProp } from 'config/getThemeForProducts';
import { roles } from 'cross-platform/utils/roleProps';
import { getThemeFont } from 'lib/styles/getThemeFont';
import {
  ButtonStates,
  getStylesForButtonStates,
} from 'lib/styles/inputStateStyles';

import { getBorderStyles } from './helpers';
import { HorizontalScaleItemProps } from './types';

const ContainerStatic = styled.label<ButtonStates>`
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  color: ${props => getStylesForButtonStates('color', props.theme)};
  border-style: solid;
  border-color: white;
  overflow: hidden;
  &:hover {
    color: ${(props): string =>
      getStylesForButtonStates('color', props.theme)({ isHovering: true })};
  }
`;

const ButtonStatic = styled.button<
  ButtonStates & ControlProps & ThemeProp & AccessibilityProps
>`
  ${getControlThemes}
  ${getThemeFont}
  appearance: none;
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  flex: 1;
  justify-content: center;
  overflow: hidden;
  margin: 0;
  border: 0;
  &:hover:not([disabled]) {
    color: ${(props): string =>
      getStylesForButtonStates(
        'color',
        props.theme
      )({ ...props, isHovering: true })};
    background-color: ${(props): string =>
      getStylesForButtonStates(
        'backgroundColor',
        props.theme
      )({
        ...props,
        isHovering: true,
      })};
  }
`;

const LabelText = styled.span`
  ${getThemeFont}
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HorizontalScaleItem = ({
  location,
  selected,
  value,
  onSelect,
  quiz,
  displayText,
  accessibilityLabel,
}: HorizontalScaleItemProps): ReactElement => {
  const [isFocused, setIsFocused] = useState(false);
  const {
    height,
    borderRadius,
    fontSize,
    iconSize,
    inputIconMarginRight,
    paddingRight,
    paddingLeft,
  } = useGetDynamicInputStyles();
  const { onFocus, onBlur, questionId } = useInputFocusContext();
  const accessibilityText =
    (accessibilityLabel ? accessibilityLabel + ' ' : '') + displayText;

  const handleBlur = () => {
    onBlur();
    if (quiz) {
      setIsFocused(false);
    }
  };

  const handleFocus = () => {
    onFocus();
    if (quiz) {
      setIsFocused(true);
    }
  };

  const uniqueId = questionId ? `${questionId}.${value}` : `${value}`;

  return (
    <ContainerStatic
      {...roles('HorizontalScaleItem')}
      isSelected={selected}
      style={{
        ...getBorderStyles(borderRadius, location),
        fontSize,
      }}
      isHovering={isFocused}
      htmlFor={uniqueId}
    >
      <ButtonStatic
        {...roles(
          `HorizontalScaleItem-input-${printableProps({ value, selected })}`,
          {
            role: 'radio',
          }
        )}
        style={{
          ...getBorderStyles(borderRadius, location),
          height,
          paddingTop: 0,
          paddingBottom: 0,
          paddingRight,
          paddingLeft,
          fontSize,
        }}
        quiz={quiz}
        disabled={typeof quiz !== 'undefined'}
        id={uniqueId}
        isSelected={selected}
        name={`${value}`}
        onBlur={handleBlur}
        onClick={quiz ? undefined : (): void => onSelect(value)}
        onFocus={handleFocus}
        type={'button'}
        value={`${value}`}
        aria-label={accessibilityText}
        aria-checked={selected}
      >
        <LabelText>{displayText}</LabelText>
      </ButtonStatic>
      <GenericInputIcon
        quiz={quiz}
        style={{
          position: 'absolute',
          top: 0.5 * (height - iconSize),
          right: inputIconMarginRight,
        }}
        size={iconSize}
      />
    </ContainerStatic>
  );
};
